// apps/web/src/shared/useDailyReview.ts
// ─────────────────────────────────────────────────────────────────────────
// Curriculum-wide review aggregation for the Daily Review entry point.
// (Flashcard spec §21.)
//
// Walks every active card from UNLOCKED lessons (lessons the user has
// finished) and reports how many are due (started + scheduled) and how many
// are brand new, plus a rough time estimate. Cards from lessons that haven't
// been completed are excluded — they aren't review debt yet. Inactive cards
// are excluded too — the same active filter the session uses.
//
// Reads localStorage synchronously (same model as the rest of the SRS shim).
// `version` lets callers force a recompute after a review session.
// ─────────────────────────────────────────────────────────────────────────

import { useMemo } from 'react';
import { LEARNING_MODULES, type Flashcard } from '@nisk/core/learning';
import { loadRecord, hasRecord, isDue } from '../lib/srs';
import {
	loadPreference,
	isLessonFlashcardsUnlocked,
} from '../lib/flashcardPrefs';
import { isLessonComplete } from './useProgress';

export interface DailyReviewSummary {
	/** Active cards that have been started AND are now scheduled (real debt). */
	dueCount: number;
	/** Active cards never reviewed yet. */
	newCount: number;
	/** dueCount + newCount, capped for display sanity. */
	totalReady: number;
	/** Rough minutes estimate (~12 cards/min floor of 1). */
	estimatedMinutes: number;
	/** Ordered card IDs to review: due (soonest first) then new. */
	cardIds: string[];
}

// Seconds-per-card heuristic for the estimate.
const SECONDS_PER_CARD = 8;

function allActiveCards(): Flashcard[] {
	const out: Flashcard[] = [];
	for (const mod of LEARNING_MODULES) {
		for (const lesson of mod.lessons) {
			// Only surface cards from lessons the user has actually finished.
			// Unlock is the canonical signal (written on lesson completion in
			// the Lesson page); isLessonComplete is a fallback for completions
			// that predate the unlock-key writes. Without this gate, every
			// untouched card in the curriculum counted as a "new" review,
			// inflating the due total to the whole flashcard corpus.
			const unlocked =
				isLessonFlashcardsUnlocked(lesson.id) ||
				isLessonComplete(mod.id, lesson.id);
			if (!unlocked) continue;

			for (const card of lesson.flashcards) {
				if (loadPreference(card.id)?.isActive === false) continue;
				out.push(card);
			}
		}
	}
	return out;
}

export function computeDailyReview(): DailyReviewSummary {
	const cards = allActiveCards();

	const dueCards: { id: string; dueAt: string }[] = [];
	const newCards: string[] = [];

	for (const card of cards) {
		if (!hasRecord(card.id)) {
			newCards.push(card.id);
			continue;
		}
		const record = loadRecord(card.id);
		if (record.state === 'new') {
			newCards.push(card.id);
		} else if (isDue(record)) {
			dueCards.push({ id: card.id, dueAt: record.dueAt });
		}
	}

	dueCards.sort((a, b) => a.dueAt.localeCompare(b.dueAt));

	const orderedIds = [...dueCards.map((c) => c.id), ...newCards];
	const totalReady = orderedIds.length;
	const estimatedMinutes = Math.max(
		1,
		Math.round((totalReady * SECONDS_PER_CARD) / 60),
	);

	return {
		dueCount: dueCards.length,
		newCount: newCards.length,
		totalReady,
		estimatedMinutes,
		cardIds: orderedIds,
	};
}

/** Memoized hook wrapper. Pass a `version` that changes to force recompute. */
export function useDailyReview(version: number = 0): DailyReviewSummary {
	// eslint-disable-next-line react-hooks/exhaustive-deps
	return useMemo(() => computeDailyReview(), [version]);
}
